import React, { useCallback, useEffect, useState } from "react";
import { InputSeen, LowQuality, pct } from "./AiEvidence";
import { api } from "./api";
import ReviewEditControls from "./ReviewEditControls";
import { Time, useTimeZone, formatTime } from "./time";
import { AttemptNote, DIM_OLDER, LatestBadge, latestFirst, newest, unsuccessfulAttempt } from "./Latest";

// Everything fetched is untrusted. All text below is rendered as React text nodes (escaped);
// there is no dangerouslySetInnerHTML and no markdown rendering anywhere in this file.

const LABEL_COLORS = { malicious: "var(--hs-danger)", suspicious: "var(--hs-warning)", benign: "var(--hs-safe)", unsure: "var(--hs-text-muted)" };

function Excerpt({ m }) {
  const mono = { fontFamily: "var(--hs-font-mono)", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" };
  return (
    <div style={{ ...mono, background: "var(--hs-code-bg)", border: "1px solid var(--hs-border)", padding: 6, marginTop: 4 }}
         data-testid="rule-excerpt">
      <span style={{ color: "var(--hs-text-muted)" }}>{m.before}</span>
      <mark className="hs-mark">{m.match}</mark>
      <span style={{ color: "var(--hs-text-muted)" }}>{m.after}</span>
      {!m.exact && <span style={{ color: "var(--hs-text-muted)" }}> (detector evidence; exact span not recorded)</span>}
    </div>
  );
}

const SOURCE_NOTE = {
  recorded: "Recorded when this verdict was written.",
  recorded_rule_ids: "Rule ids recorded with the verdict; excerpts were not recorded (older verdict).",
  inferred_from_techniques: "Older verdict: the fired rules are inferred from its recorded techniques; per-rule excerpts were not recorded.",
};

// One block of rule evidence. `kind` is "stored" (as of scoring time) or "recomputed" (from the body now).
function RulesDetail({ kind, evidence, thresholds, storedScore }) {
  const t = thresholds;
  const total = evidence.total_score ?? storedScore;
  const title = kind === "stored"
    ? "Stored with the verdict (at scoring time)"
    : "Recomputed now from the stored text with the current rules";
  return (
    <div data-testid={`rules-${kind}`} style={{ marginTop: 6, paddingLeft: 8, borderLeft: `2px solid ${kind === "stored" ? "var(--hs-accent)" : "var(--hs-text-muted)"}` }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{title}</div>
      {kind === "stored" && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{SOURCE_NOTE[evidence.source] || ""}</div>}
      <div style={{ fontSize: 12, margin: "4px 0" }}>
        Total score <strong data-testid={`${kind}-total`}>{total != null ? Number(total).toFixed(3) : "—"}</strong> vs thresholds:
        {" "}suspicious ≥ {t.suspicious}, malicious ≥ {t.malicious}, model gate ≥ {t.llm_gate}
        {" "}(configured RULES_SCORE_THRESHOLD {t.configured_llm_threshold}, capped at {t.suspicious}).
        {kind === "stored" && <div style={{ color: "var(--hs-text-muted)" }}>{t.formula}</div>}
      </div>
      {evidence.rules.length === 0 && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>No rule fired.</div>}
      {evidence.rules.map(r => (
        <div key={r.rule_id} style={{ marginBottom: 8, fontSize: 12 }} data-testid={`${kind}-rule-${r.rule_id}`}>
          <code>{r.rule_id}</code> · {r.technique} · weight {r.weight} · alone it scores {r.score}
          {r.match_count != null && <> · {r.match_count} match{r.match_count === 1 ? "" : "es"}</>}
          {(r.matches || []).map((m, i) => <Excerpt key={i} m={m} />)}
        </div>
      ))}
      {evidence.redacted_excerpt && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>Redacted evidence: {evidence.redacted_excerpt}</div>}
    </div>
  );
}

function ModelDetail({ v }) {
  return (
    <div style={{ fontSize: 12 }} data-testid="model-detail">
      <div>
        label <strong style={{ color: LABEL_COLORS[v.label] }}>{v.label}</strong>
        <LowQuality v={v} testid={`model-low-quality-${v.id}`} />
        {" "}· confidence {pct(v.confidence)} · model <code>{v.model || "not recorded"}</code>
        <InputSeen v={v} testid={`model-input-${v.id}`} />
        {" "}· cost A${(v.cost_aud ?? 0).toFixed(4)} ({v.input_tokens ?? 0} in / {v.output_tokens ?? 0} out tokens)
        {v.gate_reason && <> · sent because: {v.gate_reason}</>}
      </div>
      <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
        <span style={{ color: "var(--hs-text-muted)" }}>Reasoning: </span>{v.reasoning || "not recorded for this verdict"}
      </div>
      {v.evidence_excerpt_redacted && (
        <div style={{ color: "var(--hs-text-muted)", marginTop: 4 }}>Evidence (redacted): {v.evidence_excerpt_redacted}</div>
      )}
    </div>
  );
}

// "rules-1.5.0+prompt-1.0.0" -> "rules-1.5.0"
const rulesPack = (dv) => String(dv || "").split("+")[0];

// Rules verdicts written by an OLDER rules pack are collapsed under "History". The newest rules and model verdicts
// (by created time) carry a "Latest" badge and come first; older rules / model verdicts are dimmed. A newer model
// attempt that produced no verdict (cap, provider error, skipped) is shown next to the latest model verdict.
function Verdicts({ verdicts, thresholds, detectorVersion, attempts, ctx = {} }) {
  const current = rulesPack(detectorVersion);
  const isOld = (v) => v.source === "rules" && current && rulesPack(v.detector_version) !== current;
  const latest = {
    rules: newest(verdicts.filter((v) => v.source === "rules")),
    model: newest(verdicts.filter((v) => v.source === "model")),
  };
  const modelAttempt = unsuccessfulAttempt(attempts?.model, latest.model);
  const shown = latestFirst(verdicts.filter((v) => !isOld(v)), [latest.rules?.id, latest.model?.id]);
  const history = verdicts.filter(isOld);
  const lctx = { ...ctx, latestIds: new Set([latest.rules?.id, latest.model?.id].filter(Boolean)), modelAttempt };
  return (
    <div data-testid="evidence-verdicts">
      {modelAttempt && !latest.model && (
        <div style={{ fontSize: 12 }}><code>model</code><AttemptNote attempt={modelAttempt} testid="model-latest-attempt" /></div>)}
      <VerdictList verdicts={shown} thresholds={thresholds} ctx={lctx} />
      {history.length > 0 && (
        <details data-testid="verdict-history" style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center", color: "var(--hs-text-muted)" }}>
            History: {history.length} rules verdict{history.length === 1 ? "" : "s"} from earlier rules versions
            ({[...new Set(history.map((v) => rulesPack(v.detector_version) || "unknown"))].join(", ")})
          </summary>
          <VerdictList verdicts={history} thresholds={thresholds} ctx={ctx} />
        </details>)}
    </div>
  );
}

function VerdictList({ verdicts, thresholds, ctx = {} }) {
  return (
    <>
      {verdicts.map(v => {
        const isLatest = ctx.latestIds?.has(v.id);
        const older = !isLatest && (v.source === "rules" || v.source === "model");
        return (
        <div key={v.id} data-testid={v.outdated ? `verdict-outdated-${v.id}` : `verdict-${v.id}`} data-latest={isLatest ? "true" : undefined}
             style={{ borderTop: "1px solid var(--hs-border)", paddingTop: 8, marginTop: 8,
                      ...(older ? DIM_OLDER : {}), ...(v.outdated ? { opacity: 0.55 } : {}) }}>
          {v.outdated && (
            <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>
              <span className="hs-badge hs-badge-neutral">{v.outdated.text}</span> · not used for auto-confirm or counts
            </div>)}
          <div style={{ fontSize: 12 }}>
            {isLatest && <LatestBadge testid={`latest-${v.source}`} />}
            <code>{v.source}</code> · <span style={{ color: LABEL_COLORS[v.label] }}>{v.label}</span>
            {v.detector_version && <> · <code>{rulesPack(v.detector_version)}</code></>}
            {v.score != null && <> · score {v.score}</>}
            {(v.techniques || []).length > 0 && <> · {v.techniques.join(", ")}</>}
            {" "}· <Time value={v.created_at} />
            {!v.on_selected_snapshot && <span style={{ color: "var(--hs-text-muted)" }}> · (other snapshot)</span>}
            {isLatest && v.source === "model" && <AttemptNote attempt={ctx.modelAttempt} testid="model-latest-attempt" />}
          </div>
          {v.source === "rules" && v.stored_evidence && (
            <RulesDetail kind="stored" evidence={v.stored_evidence} thresholds={thresholds} storedScore={v.score} />
          )}
          {v.source === "rules" && v.recomputed_evidence && (
            <>
              <RulesDetail kind="recomputed" evidence={v.recomputed_evidence} thresholds={thresholds} storedScore={v.score} />
              {v.score != null && Math.abs((v.recomputed_evidence.total_score ?? 0) - v.score) > 0.0005 && (
                <div style={{ fontSize: 12, color: "var(--hs-warning)" }}>
                  Recomputed score differs from the stored one: the rules have changed since this verdict was written.
                </div>
              )}
            </>
          )}
          {v.source === "rules" && !v.recomputed_evidence && v.on_selected_snapshot && (
            <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>Not recomputed: the snapshot body is missing, so only the stored evidence is shown.</div>
          )}
          {v.source === "model" && <ModelDetail v={v} />}
          {v.source === "human" && (
            <div style={{ fontSize: 12 }}>by {v.actor || "—"}{v.note ? ` — ${v.note}` : ""}
              {v.review_state && v.review_state !== "active" && <span style={{ color: "var(--hs-text-muted)" }}> · {v.review_state === "withdrawn" ? "undone" : "edited"}</span>}
              {v.review_state === "active" && v.label !== "withdrawn" && ctx.styles && (
                <details style={{ marginTop: 4 }}><summary style={{ cursor: "pointer" }} data-testid={`edit-human-${v.id}`}>Edit / undo this label</summary>
                  <ReviewEditControls verdict={{ ...v, verdict_id: v.id, state: "active" }} styles={ctx.styles} onChanged={ctx.onChanged} />
                </details>)}
            </div>
          )}
        </div>
        );
      })}
    </>
  );
}

function DiffView({ diff, unavailable }) {
  if (!diff && unavailable) return <div style={{ color: "var(--hs-warning)", fontSize: 12 }} data-testid="diff-unavailable">Diff unavailable: {unavailable}.</div>;
  if (!diff) return <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>First snapshot — nothing to compare.</div>;
  if (diff.empty) return <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>No difference from the previous snapshot.</div>;
  return (
    <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }} data-testid="evidence-diff">
      {diff.text.split("\n").map((line, i) => {
        const color = line.startsWith("+") && !line.startsWith("+++") ? "var(--hs-safe)"
          : line.startsWith("-") && !line.startsWith("---") ? "var(--hs-danger)" : "var(--hs-text-muted)";
        return <div key={i} style={{ color }}>{line}</div>;
      })}
    </pre>
  );
}

export default function EvidencePanel({ itemId, styles, onChanged, refresh = 0 }) {
  const { tz } = useTimeZone();
  const [ev, setEv] = useState(null);
  const [full, setFull] = useState(false);
  const [snapId, setSnapId] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [job, setJob] = useState(null);

  const load = useCallback(() => api.itemEvidence(itemId, { full, snapshotId: snapId })
    .then(r => { setEv(r.data); setErr(""); })
    .catch(ex => setErr(ex.message)), [itemId, full, snapId, refresh]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const label = async (verdict) => {
    if (!ev?.snapshot?.is_current) { setMsg("Select the current snapshot to label."); return; }
    // A malicious label can make the item public (confirmed): ask first.
    if (verdict === "malicious" && !window.confirm("Label this item MALICIOUS? This confirms it and can make it public. It is recorded and audited.")) return;
    try {
      const r = await api.humanVerdict(itemId, { verdict, snapshot_id: ev.snapshot.id, note });
      setMsg(`Recorded "${verdict}" — tier is now ${r.data.tier}.`);
      setNote("");
      await load();
      if (onChanged) onChanged();
    } catch (ex) { setMsg("Error: " + ex.message); }
  };

  const askForce = async () => {
    try { const r = await api.rescoreEstimate({ item_ids: [itemId], force: true }); setEstimate(r.data); }
    catch (ex) { setMsg("Error: " + ex.message); }
  };
  const runRescore = async (force) => {
    try {
      const r = await api.rescore({ item_ids: [itemId], force });
      setEstimate(null);
      setJob({ status: "queued", job_id: r.data.job_id });
    } catch (ex) { setMsg("Error: " + ex.message); }
  };
  useEffect(() => {
    if (!job || ["done", "failed"].includes(job.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.rescoreStatus(job.job_id);
        setJob(r.data);
        if (["done", "failed"].includes(r.data.status)) { clearInterval(t); load(); if (onChanged) onChanged(); }
      } catch (_) { clearInterval(t); }
    }, 500);
    return () => clearInterval(t);
  }, [job, load, onChanged]);

  if (err) return <div style={{ ...styles.card, color: "var(--hs-danger)" }}>Could not load evidence: {err}</div>;
  if (!ev) return <div style={styles.card}>Loading evidence…</div>;
  const s = ev.snapshot;
  return (
    <div data-testid="evidence-panel">
      <div style={styles.card} data-testid="label-bar">
        <h3 style={{ marginTop: 0 }}>Label this item</h3>
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          Tier <strong>{ev.item.current_tier}</strong>
          {ev.item.top_rule && <> · top rule <code>{ev.item.top_rule}</code></>}
          {ev.item.rules_score != null && <> · rules score {ev.item.rules_score}</>}
          {(ev.item.payment_authorization || []).length > 0 && (
            <> · payment authorization:{" "}
              {ev.item.payment_authorization.map((t) => <code key={t} data-testid={`item-payment-${t}`} style={{ marginRight: 4 }}>{t}</code>)}</>)}
        </div>
        <input data-testid="label-note" style={{ ...styles.input, width: 360, marginRight: 8 }} placeholder="note (optional)"
               value={note} onChange={e => setNote(e.target.value)} />
        {["benign", "unsure", "malicious"].map((l) => {
          const current = ev.current_human_label?.label === l;
          const danger = l === "malicious";
          const style = {
            ...styles.btnDim,
            ...(danger ? { color: "var(--hs-danger)", borderColor: "var(--hs-danger)" } : {}),
            ...(current ? { borderWidth: 2, borderColor: danger ? "var(--hs-danger)" : "var(--hs-accent)", fontWeight: 700 } : {}),
          };
          return (
            <React.Fragment key={l}>
              <button data-testid={`label-${l}`} data-variant={danger ? "danger" : "neutral"} aria-pressed={current}
                      style={style} onClick={() => label(l)}>
                Label: {l}{current ? " ✓ (current)" : ""}</button>{" "}
            </React.Fragment>);
        })}
        {msg && <div style={{ marginTop: 8, fontSize: 12, color: "var(--hs-text-muted)" }} data-testid="label-msg">{msg}</div>}
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <button data-testid="item-rescore" style={styles.btnDim} onClick={() => runRescore(false)}>Re-score</button>{" "}
          <button data-testid="item-force-estimate" style={styles.btnDim} onClick={askForce}>Force re-score (ask the model)…</button>
        </div>
        {estimate && (
          <div style={{ marginTop: 8, fontSize: 12, border: "1px solid var(--hs-border)", padding: 8 }} data-testid="force-estimate">
            {estimate.llm_available ? (
              <>This would send <strong>{estimate.would_call_llm}</strong> of {estimate.total} snapshot(s)
                {estimate.missing_content > 0 && <span style={{ color: "var(--hs-danger)" }}> ({estimate.missing_content} skipped: content missing)</span>} to
                {" "}<code>{estimate.model}</code>, est. <strong>A${estimate.estimated_cost_aud}</strong>
                {" "}(≈{estimate.est_input_tokens} in / {estimate.est_output_tokens} out tokens).
                {" "}Daily cap A${estimate.daily_cap_aud}, spent A${estimate.spent_today_aud}, A${estimate.cap_remaining_aud} left.
                {estimate.would_exceed_cap && <span style={{ color: "var(--hs-danger)" }}> This exceeds the remaining cap; the run will stop at the cap.</span>}</>
            ) : <>No Anthropic key is configured: the free mock model would be used (A$0).</>}
            {" "}<button data-testid="force-run" style={styles.btn} onClick={() => runRescore(true)}>Run forced re-score</button>{" "}
            <button style={styles.btnDim} onClick={() => setEstimate(null)}>Cancel</button>
          </div>
        )}
        {job && (
          <div style={{ marginTop: 8, fontSize: 12 }} data-testid="item-rescore-job">
            Re-score {job.status}
            {job.missing_content > 0 && <> · {job.missing_content} skipped: content missing</>}
            {job.llm && <> · model: {job.llm.ok} ok, {job.llm.error} error, {job.llm.cap} cap-stopped · {Object.entries(job.llm.reasons || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "—"}</>}
          </div>
        )}
      </div>

      {ev.reports && ev.reports.length > 0 && (
        <div style={{ ...styles.card, borderColor: "var(--hs-warning)" }} data-testid="item-reports">
          <h3 style={{ marginTop: 0 }}>User reports ({ev.reports.length})</h3>
          {ev.reports.map(r => (
            <div key={r.id} style={{ fontSize: 12, marginBottom: 8, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              <span style={{ color: "var(--hs-text-muted)" }}><Time value={r.created_at} /> · {r.status}{r.has_email ? " · reporter left an email" : ""} · reported as “{r.server_input}”</span>
              <div>{r.what_happened}</div>
            </div>))}
          <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>Reports only raise review priority; they never change the tier.</div>
        </div>
      )}

      <div style={styles.card} data-testid="snapshot-card">
        <h3 style={{ marginTop: 0 }}>Snapshot content</h3>
        <div style={{ fontSize: 12, marginBottom: 6 }}>
          <select data-testid="snapshot-select" style={styles.select} value={s?.id || ""}
                  onChange={e => { setSnapId(e.target.value); setFull(false); }}>
            {ev.snapshots.map(x => (
              <option key={x.id} value={x.id}>{formatTime(x.fetched_at, tz)} · {x.size_bytes}B · {(x.content_hash || "").slice(0, 8)}</option>
            ))}
          </select>
          {s && !s.content_missing && <> {s.is_current ? "current" : "older"} · {s.format === "json" ? "pretty-printed JSON" : "plain text"} ·
            {" "}showing {s.chars_shown} of {s.chars_total} characters</>}
          {s && s.content_truncated && <span style={{ color: "var(--hs-warning)" }}> · stored truncated to 256 KB</span>}
        </div>
        {s && s.content_missing ? (
          <div data-testid="content-missing" style={{ border: "1px solid var(--hs-danger)", background: "var(--hs-danger-subtle)", padding: 8, fontSize: 12 }}>
            <strong style={{ color: "var(--hs-danger)" }}>Content missing.</strong> The body of this snapshot ({s.size_bytes} bytes recorded)
            is not in storage, so it cannot be shown, diffed or re-scored. The verdicts below still show the evidence recorded
            when they were scored. Use <em>Sources → Refetch missing content</em> to restore it.
          </div>
        ) : s ? (
          <>
            <pre data-testid="snapshot-text" style={{ margin: 0, maxHeight: 420, overflow: "auto", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", background: "var(--hs-code-bg)", padding: 8, border: "1px solid var(--hs-border)" }}>{s.text}</pre>
            {(s.truncated || full) && (
              <button data-testid="show-full" style={{ ...styles.btnDim, marginTop: 6 }} onClick={() => setFull(!full)}>
                {full ? "Show first 20,000 characters only" : `Show full (${s.chars_total} characters)`}
              </button>
            )}
          </>
        ) : <div style={{ color: "var(--hs-text-muted)" }}>No snapshot yet.</div>}
      </div>

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Diff vs previous snapshot</h3>
        <DiffView diff={ev.diff} unavailable={ev.diff_unavailable} />
      </div>

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Verdicts &amp; evidence ({ev.verdicts.length})</h3>
        <Verdicts verdicts={ev.verdicts} thresholds={ev.thresholds} detectorVersion={ev.detector_version}
                  attempts={ev.item.ai_attempts}
                  ctx={{ styles, onChanged: () => { load(); onChanged && onChanged(); } }} />
      </div>
    </div>
  );
}
